/**
 * Client-side Stripe utilities
 */

type PlanTier = 'plus' | 'pro';

export interface CheckoutResult {
  url?: string;
  upgraded?: boolean;
  tier?: string;
  error?: string;
}

/**
 * Start the Stripe checkout flow or upgrade existing subscription
 * Returns the result - caller handles redirect or UI update
 */
export async function startCheckout(
  tier: PlanTier,
  returnTo: string = '/',
  wasPlaying: boolean = false
): Promise<CheckoutResult> {
  try {
    const response = await fetch('/api/stripe/create-checkout-session', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tier, returnTo, wasPlaying }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || 'Failed to start checkout' };
    }

    // If upgraded in-place (existing subscription updated, or gift fallback)
    // Server returns { upgraded: true, tier } in both cases
    if (data.upgraded) {
      return { upgraded: true, tier: data.tier };
    }

    // New subscription - redirect to Stripe checkout
    if (data.url) {
      window.location.href = data.url;
      return { url: data.url };
    }

    return { error: 'Unexpected response from server' };
  } catch (error) {
    console.error('Checkout error:', error);
    return { error: 'Network error' };
  }
}

/**
 * Open the Stripe Customer Portal for subscription management
 */
export async function openCustomerPortal(
  returnTo: string = '/profile'
): Promise<void> {
  try {
    const response = await fetch('/api/stripe/create-portal-session', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnTo }),
    });

    const data = await response.json();

    if (data.url) {
      window.location.href = data.url;
    } else {
      console.error('Failed to create portal session:', data.error);
    }
  } catch (error) {
    console.error('Portal error:', error);
  }
}

/**
 * Verify a Stripe checkout session completed successfully
 */
export async function verifyCheckoutSession(
  sessionId: string
): Promise<{ verified: boolean; tier?: string }> {
  try {
    const response = await fetch(
      `/api/stripe/verify-session?session_id=${encodeURIComponent(sessionId)}`,
      { credentials: 'include' }
    );
    return await response.json();
  } catch {
    return { verified: false };
  }
}
